import React, { useCallback, useEffect, useState } from "react";
import { HERO_STATS, HOMEPAGE_SAMPLE_WISTIA_ID } from "../constants";
import {
  HeroSection,
  HeroGlow,
  HeroBlurOrb,
  HeroGrid,
  HeroBottomFade,
  HeroInner,
  HeroBadge,
  HeroBadgeDot,
  HeroBadgeText,
  HeroTitle,
  HeroLead,
  HeroActions,
  HeroCta,
  HeroOutlineCta,
  HeroSampleScrim,
  HeroSampleCard,
  HeroSampleVideo,
  HeroSampleClose,
  StatsRow,
  StatCard,
  StatIcon,
  StatLabel,
  StatSublabel,
} from "./Hero.styles";

const PlayGlyph = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <polygon points="6 3 20 12 6 21 6 3" />
  </svg>
);

const playSampleWistia = (mediaId) => {
  if (!mediaId) return;

  const tryPlay = () => {
    const video = window.Wistia?.api?.(mediaId);
    if (!video || typeof video.play !== "function") return false;
    try {
      video.play();
      return true;
    } catch (_) {
      return false;
    }
  };

  window._wq = window._wq || [];
  window._wq.push({
    id: mediaId,
    options: {
      playButton: true,
      videoFoam: true,
      playsinline: true,
      autoPlay: true,
    },
    onReady: (video) => {
      try {
        video?.play?.();
      } catch (_) {
        /* ignore */
      }
    },
  });

  if (tryPlay()) return;
  window.setTimeout(tryPlay, 250);
  window.setTimeout(tryPlay, 700);
  if (window.Wistia?.embeds?.setup) {
    window.Wistia.embeds.setup();
  }
};

const Hero = ({ onSubscribeClick }) => {
  const [sampleOpen, setSampleOpen] = useState(false);

  const closeSample = useCallback(() => setSampleOpen(false), []);

  useEffect(() => {
    if (!sampleOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") closeSample();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    playSampleWistia(HOMEPAGE_SAMPLE_WISTIA_ID);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [sampleOpen, closeSample]);

  return (
    <HeroSection>
      <HeroGlow aria-hidden="true" />
      <HeroBlurOrb aria-hidden="true" />
      <HeroGrid aria-hidden="true" />
      <HeroBottomFade aria-hidden="true" />

      <HeroInner>
        <HeroBadge>
          <HeroBadgeDot aria-hidden="true" />
          <HeroBadgeText>Live Market Analysis</HeroBadgeText>
        </HeroBadge>

        <HeroTitle>
          Expert Bitcoin and Crypto Currencies Insights.{" "}
          <span>Real-Time.</span>
        </HeroTitle>

        <HeroLead>
          7 world-class financial analysts delivering institutional-grade crypto
          market commentary, exclusively for subscribers.
        </HeroLead>

        <HeroActions>
          <HeroCta type="button" onClick={onSubscribeClick}>
            Start Your Subscription
          </HeroCta>
          <HeroOutlineCta
            type="button"
            onClick={() => setSampleOpen(true)}
            aria-haspopup="dialog"
          >
            <PlayGlyph />
            Watch Sample Analysis
          </HeroOutlineCta>
        </HeroActions>

        <StatsRow>
          {HERO_STATS.map((stat) => (
            <StatCard key={stat.label}>
              <StatIcon aria-hidden="true">
                <span className="material-icons">{stat.icon}</span>
              </StatIcon>
              <StatLabel>{stat.label}</StatLabel>
              <StatSublabel>{stat.sublabel}</StatSublabel>
            </StatCard>
          ))}
        </StatsRow>
      </HeroInner>

      {sampleOpen ? (
        <HeroSampleScrim role="presentation" onClick={closeSample}>
          <HeroSampleCard
            role="dialog"
            aria-modal="true"
            aria-label="Sample analysis"
            onClick={(event) => event.stopPropagation()}
          >
            <HeroSampleClose
              type="button"
              aria-label="Close sample analysis"
              onClick={closeSample}
            >
              <span className="material-icons" aria-hidden="true">
                close
              </span>
            </HeroSampleClose>
            <HeroSampleVideo>
              <div
                key="sample-open"
                className={`wistia_embed wistia_async_${HOMEPAGE_SAMPLE_WISTIA_ID} videoFoam=true`}
              />
            </HeroSampleVideo>
          </HeroSampleCard>
        </HeroSampleScrim>
      ) : null}
    </HeroSection>
  );
};

export default Hero;
